import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputLabel,
  TextField,
  FormControlLabel,
  Switch,
  FormGroup,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  pageShareAPI,
  PageShareInfo,
  PageShareType,
} from "../../services/api/api";
import { Debt, Expense, Income, ExpenseSection } from "../../lib/types";
import ShareLinkDialog, { ShareOption } from "./ShareLinkDialog";

type ShareType = "public" | "password" | "auth";

interface PageShareModalProps {
  open: boolean;
  onClose: () => void;
  pageType: PageShareType;
  payload?: any;
  label?: string | null;
  buildShareUrl?: (shareId: string) => string;
  debtItems?: Debt[];
  expenseItems?: Expense[];
  incomeItems?: Income[];
}

type DebtShareSettings = {
  hiddenDebtIds: string[];
  hiddenTypes: string[];
  totalExcludedTypes: string[];
  visibleCards: DebtShareCardKey[];
  hideLimits: boolean;
  hideDueDates: boolean;
  hideDateBorrowed: boolean;
  creditorNameMode: "full" | "partial" | "obscured";
  accountNameMode: "full" | "partial" | "obscured";
};

type DebtShareCardKey =
  | "balance"
  | "creditLimit"
  | "available"
  | "utilization"
  | "paydown"
  | "monthly"
  | "credits";

type ExpenseShareSettings = {
  hiddenExpenseIds: string[];
  hiddenSections: ExpenseSection[];
};

type IncomeShareSettings = {
  hiddenIncomeIds: string[];
  hiddenSections: ExpenseSection[];
};

type ExpirationOption = "1day" | "7days" | "30days" | "never" | "custom";

const DEFAULT_TOTAL_BALANCE_EXCLUDED_TYPES = ["Auto Loan", "Mortgage"];
const DEFAULT_DEBT_VISIBLE_CARDS: DebtShareCardKey[] = [
  "balance",
  "creditLimit",
  "available",
  "utilization",
  "paydown",
  "monthly",
  "credits",
];

const defaultDebtSettings: DebtShareSettings = {
  hiddenDebtIds: [],
  hiddenTypes: [],
  totalExcludedTypes: DEFAULT_TOTAL_BALANCE_EXCLUDED_TYPES,
  visibleCards: DEFAULT_DEBT_VISIBLE_CARDS,
  hideLimits: false,
  hideDueDates: false,
  hideDateBorrowed: false,
  creditorNameMode: "full",
  accountNameMode: "full",
};

const defaultExpenseSettings: ExpenseShareSettings = {
  hiddenExpenseIds: [],
  hiddenSections: [],
};

const defaultIncomeSettings: IncomeShareSettings = {
  hiddenIncomeIds: [],
  hiddenSections: [],
};

const PageShareModal: React.FC<PageShareModalProps> = ({
  open,
  onClose,
  pageType,
  payload,
  label,
  buildShareUrl: buildShareUrlOverride,
  debtItems = [],
  expenseItems = [],
  incomeItems = [],
}) => {
  const [shareType, setShareType] = useState<ShareType>("public");
  const [password, setPassword] = useState("");
  const [shareName, setShareName] = useState("");
  const [shares, setShares] = useState<PageShareInfo[]>([]);
  const [generatedLink, setGeneratedLink] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [expirationOption, setExpirationOption] =
    useState<ExpirationOption>("30days");
  const [customExpiration, setCustomExpiration] = useState("");
  const [debtSettings, setDebtSettings] =
    useState<DebtShareSettings>(defaultDebtSettings);
  const [expenseSettings, setExpenseSettings] = useState<ExpenseShareSettings>(
    defaultExpenseSettings
  );
  const [incomeSettings, setIncomeSettings] = useState<IncomeShareSettings>(
    defaultIncomeSettings
  );
  const [editingShare, setEditingShare] = useState<PageShareInfo | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingSettings, setEditingSettings] =
    useState<DebtShareSettings>(defaultDebtSettings);
  const [editingExpenseSettings, setEditingExpenseSettings] =
    useState<ExpenseShareSettings>(defaultExpenseSettings);
  const [editingIncomeSettings, setEditingIncomeSettings] =
    useState<IncomeShareSettings>(defaultIncomeSettings);

  const buildShareUrl = (shareId: string) => {
    if (typeof buildShareUrlOverride === "function") {
      return buildShareUrlOverride(shareId);
    }
    return `${window.location.origin}/shared/${pageType}/${shareId}`;
  };

  const loadShares = useCallback(async () => {
    try {
      console.debug("[PageShareModal] Loading shares", { pageType });
      const data = await pageShareAPI.list(pageType);
      setShares(data);
      console.debug("[PageShareModal] Loaded shares", {
        pageType,
        count: data.length,
      });
    } catch (err) {
      console.error("[PageShareModal] Failed to list shares", err);
    }
  }, [pageType]);

  useEffect(() => {
    if (!open) return;
    setGeneratedLink("");
    setError("");
    setPassword("");
    setShareType("public");
    setShareName(label || "");
    setDebtSettings(defaultDebtSettings);
    setExpenseSettings(defaultExpenseSettings);
    setIncomeSettings(defaultIncomeSettings);
    setExpirationOption("30days");
    setCustomExpiration("");
    loadShares();
  }, [open, pageType, label, loadShares]);

  const handleCreate = async () => {
    setError("");
    if (!shareName.trim()) {
      setError("Name is required for share links.");
      return;
    }
    if ((pageType === "forecasting" || pageType === "wife") && !payload) {
      setError("Select a profile before creating a share link.");
      return;
    }
    if (shareType === "password" && !password.trim()) {
      setError("Password required for protected link.");
      return;
    }
    if (expirationOption === "custom" && !customExpiration.trim()) {
      setError("Custom expiration date is required.");
      return;
    }
    try {
      setCreating(true);
      console.debug("[PageShareModal] Creating share", {
        pageType,
        shareType,
        hasPayload: Boolean(payload),
        label,
      });
      const expiresAt = (() => {
        const now = new Date();
        switch (expirationOption) {
          case "1day":
            return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
          case "7days":
            return new Date(
              now.getTime() + 7 * 24 * 60 * 60 * 1000
            ).toISOString();
          case "30days":
            return new Date(
              now.getTime() + 30 * 24 * 60 * 60 * 1000
            ).toISOString();
          case "custom":
            return new Date(customExpiration).toISOString();
          case "never":
          default:
            return null;
        }
      })();

      const payloadToSend =
        pageType === "debts"
          ? { debtSettings }
          : pageType === "expenses"
            ? { expenseSettings }
            : pageType === "income"
              ? { incomeSettings }
              : payload || undefined;

      const response = await pageShareAPI.create({
        pageType,
        password: shareType === "password" ? password.trim() : undefined,
        isPublic: shareType === "public" || shareType === "password",
        requiresAuth: shareType === "auth",
        payload: payloadToSend,
        label: shareName.trim(),
        expiresAt,
      });
      const link = buildShareUrl(response.shareId);
      setGeneratedLink(link);
      console.debug("[PageShareModal] Share created", {
        pageType,
        shareId: response.shareId,
      });
      await loadShares();
    } catch (err) {
      console.error("[PageShareModal] Failed to create share", err);
      setError("Failed to create share.");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (link: string) => {
    await navigator.clipboard.writeText(link);
    console.debug("[PageShareModal] Copied share link");
  };

  const handleDelete = async (shareId: string) => {
    try {
      console.debug("[PageShareModal] Deleting share", { shareId });
      await pageShareAPI.delete(shareId);
      setShares((prev) => prev.filter((share) => share.id !== shareId));
      console.debug("[PageShareModal] Deleted share", { shareId });
    } catch (err) {
      console.error("[PageShareModal] Failed to delete share", err);
    }
  };

  const handleEditOpen = (share: PageShareInfo) => {
    const existingDebtSettings =
      share.payload?.debtSettings || defaultDebtSettings;
    const existingExpenseSettings =
      share.payload?.expenseSettings || defaultExpenseSettings;
    const existingIncomeSettings =
      share.payload?.incomeSettings || defaultIncomeSettings;
    setEditingShare(share);
    setEditingName(share.label || "");
    setEditingSettings({
      ...defaultDebtSettings,
      ...existingDebtSettings,
    });
    setEditingExpenseSettings({
      ...defaultExpenseSettings,
      ...existingExpenseSettings,
    });
    setEditingIncomeSettings({
      ...defaultIncomeSettings,
      ...existingIncomeSettings,
    });
  };

  const handleEditSave = async () => {
    if (!editingShare) return;
    if (!editingName.trim()) {
      setError("Name is required for share links.");
      return;
    }
    try {
      const payloadToSend =
        pageType === "debts"
          ? { debtSettings: editingSettings }
          : pageType === "expenses"
            ? { expenseSettings: editingExpenseSettings }
            : pageType === "income"
              ? { incomeSettings: editingIncomeSettings }
              : editingShare.payload || null;
      await pageShareAPI.update(editingShare.id, {
        label: editingName.trim(),
        payload: payloadToSend,
      });
      await loadShares();
      setEditingShare(null);
    } catch (err) {
      console.error("[PageShareModal] Failed to update share", err);
      setError("Failed to update share.");
    }
  };

  const debtTypes = useMemo(() => {
    const values = new Set<string>();
    debtItems.forEach((debt) => {
      if (debt.type) values.add(debt.type);
    });
    return Array.from(values).sort();
  }, [debtItems]);

  const debtOptions = useMemo(
    () =>
      debtItems.map((debt) => ({
        id: debt.id,
        label:
          debt.creditor && debt.account
            ? `${debt.creditor} • ${debt.account}`
            : debt.creditor || debt.account || debt.id,
      })),
    [debtItems]
  );

  const expenseOptions = useMemo(
    () =>
      (expenseItems || []).map((expense) => ({
        id: expense.id,
        label: expense.vendor
          ? `${expense.name} • ${expense.vendor}`
          : expense.name || expense.id,
      })),
    [expenseItems]
  );

  const incomeOptions = useMemo(
    () =>
      (incomeItems || []).map((item) => ({
        id: item.id,
        label: item.client
          ? `${item.client} • ${item.invoiceNumber || "Invoice"}`
          : item.invoiceNumber || item.id,
      })),
    [incomeItems]
  );

  const sectionOptions: Array<{ key: ExpenseSection; label: string }> = [
    { key: "overdue", label: "Overdue" },
    { key: "today", label: "Today" },
    { key: "tomorrow", label: "Tomorrow" },
    { key: "thisWeek", label: "Within 7 Days" },
    { key: "next10Days", label: "Within 10 Days" },
    { key: "expected", label: "Within 30 Days" },
  ];

  const describeShare = (share: PageShareInfo) => {
    if (share.requiresAuth) return "Auth only";
    if (share.requiresPassword) return "Password";
    return share.isPublic ? "Public" : "Private";
  };

  const formatShareDateTime = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear() % 100;
    const hourMinute = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const getSuffix = (num: number) => {
      if (num >= 11 && num <= 13) return "th";
      switch (num % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${month}. ${day}${getSuffix(day)}, '${year
      .toString()
      .padStart(2, "0")} ${hourMinute}`;
  };

  const shareOptions: ShareOption[] = [
    { value: "public", label: "Public link" },
    { value: "password", label: "Password protected" },
    { value: "auth", label: "Authenticated only" },
  ];

  const debtCardOptions: Array<{ key: DebtShareCardKey; label: string }> = [
    { key: "balance", label: "Total Balance" },
    { key: "creditLimit", label: "Total Credit Limit" },
    { key: "available", label: "Available Credit" },
    { key: "utilization", label: "Credit Utilization" },
    { key: "paydown", label: "Paydown Target" },
    { key: "monthly", label: "Monthly Payments" },
    { key: "credits", label: "Statement Credits" },
  ];

  const renderDebtSettings = (
    settings: DebtShareSettings,
    onChange: (next: DebtShareSettings) => void
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">Debt Share Settings</Typography>
      <FormControl fullWidth size="small">
        <InputLabel>Obscure Creditor Names</InputLabel>
        <Select
          value={settings.creditorNameMode}
          label="Obscure Creditor Names"
          onChange={(e) =>
            onChange({
              ...settings,
              creditorNameMode: e.target
                .value as DebtShareSettings["creditorNameMode"],
            })
          }
        >
          <MenuItem value="full">Show full</MenuItem>
          <MenuItem value="partial">Partial (Aff...)</MenuItem>
          <MenuItem value="obscured">Obscured (Debtor 1)</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth size="small">
        <InputLabel>Obscure Account Names</InputLabel>
        <Select
          value={settings.accountNameMode}
          label="Obscure Account Names"
          onChange={(e) =>
            onChange({
              ...settings,
              accountNameMode: e.target
                .value as DebtShareSettings["accountNameMode"],
            })
          }
        >
          <MenuItem value="full">Show full</MenuItem>
          <MenuItem value="partial">Partial (Acc...)</MenuItem>
          <MenuItem value="obscured">Obscured (Account 1)</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={settings.hideLimits}
            onChange={(e) =>
              onChange({ ...settings, hideLimits: e.target.checked })
            }
          />
        }
        label="Hide credit limits"
      />
      <FormControlLabel
        control={
          <Switch
            checked={settings.hideDateBorrowed}
            onChange={(e) =>
              onChange({ ...settings, hideDateBorrowed: e.target.checked })
            }
          />
        }
        label="Hide date borrowed"
      />
      <FormControlLabel
        control={
          <Switch
            checked={settings.hideDueDates}
            onChange={(e) =>
              onChange({ ...settings, hideDueDates: e.target.checked })
            }
          />
        }
        label="Hide due dates"
      />
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Total Balance Card Types
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Toggle which debt types are included in the Total Balance card.
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel>Included Types</InputLabel>
        <Select
          multiple
          value={debtTypes.filter(
            (type) => !settings.totalExcludedTypes.includes(type)
          )}
          label="Included Types"
          onChange={(e) => {
            const included = e.target.value as string[];
            const excluded = debtTypes.filter(
              (type) => !included.includes(type)
            );
            onChange({
              ...settings,
              totalExcludedTypes: excluded,
            });
          }}
          renderValue={(selected) =>
            (selected as string[]).length
              ? (selected as string[]).join(", ")
              : "None"
          }
        >
          {debtTypes.map((type) => {
            const included = !settings.totalExcludedTypes.includes(type);
            return (
              <MenuItem key={type} value={type}>
                <Checkbox checked={included} />
                <ListItemText primary={type} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Visible Summary Cards
      </Typography>
      <FormGroup>
        {debtCardOptions.map((option) => (
          <FormControlLabel
            key={option.key}
            control={
              <Checkbox
                size="small"
                checked={settings.visibleCards.includes(option.key)}
                onChange={(e) => {
                  const next = new Set(settings.visibleCards);
                  if (e.target.checked) {
                    next.add(option.key);
                  } else {
                    next.delete(option.key);
                  }
                  onChange({
                    ...settings,
                    visibleCards: Array.from(next),
                  });
                }}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
      <FormControl fullWidth size="small">
        <InputLabel>Hide Debt Types</InputLabel>
        <Select
          multiple
          value={settings.hiddenTypes}
          label="Hide Debt Types"
          onChange={(e) =>
            onChange({
              ...settings,
              hiddenTypes: e.target.value as string[],
            })
          }
          renderValue={(selected) =>
            (selected as string[]).length
              ? (selected as string[]).join(", ")
              : "None"
          }
        >
          {debtTypes.map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox checked={settings.hiddenTypes.includes(type)} />
              <ListItemText primary={type} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth size="small">
        <InputLabel>Hide Specific Debts</InputLabel>
        <Select
          multiple
          value={settings.hiddenDebtIds}
          label="Hide Specific Debts"
          onChange={(e) =>
            onChange({
              ...settings,
              hiddenDebtIds: e.target.value as string[],
            })
          }
          renderValue={(selected) => {
            const ids = selected as string[];
            if (!ids.length) return "None";
            return ids
              .map(
                (id) => debtOptions.find((option) => option.id === id)?.label
              )
              .filter(Boolean)
              .join(", ");
          }}
        >
          {debtOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox checked={settings.hiddenDebtIds.includes(option.id)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderSectionVisibility = (
    settings: { hiddenSections: ExpenseSection[] },
    onChange: (next: ExpenseSection[]) => void
  ) => {
    const hiddenSet = new Set(settings.hiddenSections);
    const futureMonthsHidden =
      hiddenSet.has("nextMonth") && hiddenSet.has("upcoming");

    const toggleSection = (section: ExpenseSection, checked: boolean) => {
      const next = new Set(hiddenSet);
      if (checked) {
        next.add(section);
      } else {
        next.delete(section);
      }
      onChange(Array.from(next));
    };

    const toggleFutureMonths = (checked: boolean) => {
      const next = new Set(hiddenSet);
      if (checked) {
        next.add("nextMonth");
        next.add("upcoming");
      } else {
        next.delete("nextMonth");
        next.delete("upcoming");
      }
      onChange(Array.from(next));
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle2">Hide Date Ranges</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={futureMonthsHidden}
              onChange={(e) => toggleFutureMonths(e.target.checked)}
            />
          }
          label="Future Months"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          {sectionOptions.map((option) => (
            <FormControlLabel
              key={option.key}
              control={
                <Checkbox
                  checked={hiddenSet.has(option.key)}
                  onChange={(e) => toggleSection(option.key, e.target.checked)}
                />
              }
              label={option.label}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderExpenseSettings = (
    settings: ExpenseShareSettings,
    onChange: (next: ExpenseShareSettings) => void
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">Expense Share Settings</Typography>
      {renderSectionVisibility(settings, (hiddenSections) =>
        onChange({ ...settings, hiddenSections })
      )}
      <FormControl fullWidth size="small">
        <InputLabel>Hide Specific Expenses</InputLabel>
        <Select
          multiple
          value={settings.hiddenExpenseIds}
          label="Hide Specific Expenses"
          onChange={(e) =>
            onChange({
              ...settings,
              hiddenExpenseIds: e.target.value as string[],
            })
          }
          renderValue={(selected) =>
            expenseOptions
              .filter((option) => (selected as string[]).includes(option.id))
              .map((option) => option.label)
              .join(", ")
          }
        >
          {expenseOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox
                checked={settings.hiddenExpenseIds.includes(option.id)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderIncomeSettings = (
    settings: IncomeShareSettings,
    onChange: (next: IncomeShareSettings) => void
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">Income Share Settings</Typography>
      {renderSectionVisibility(settings, (hiddenSections) =>
        onChange({ ...settings, hiddenSections })
      )}
      <FormControl fullWidth size="small">
        <InputLabel>Hide Specific Income</InputLabel>
        <Select
          multiple
          value={settings.hiddenIncomeIds}
          label="Hide Specific Income"
          onChange={(e) =>
            onChange({
              ...settings,
              hiddenIncomeIds: e.target.value as string[],
            })
          }
          renderValue={(selected) =>
            incomeOptions
              .filter((option) => (selected as string[]).includes(option.id))
              .map((option) => option.label)
              .join(", ")
          }
        >
          {incomeOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox
                checked={settings.hiddenIncomeIds.includes(option.id)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const shareDialogTitle = (() => {
    if (pageType === "forecasting") return "Share Forecast Profile";
    if (pageType === "housing") return "Share Housing Forecast";
    const label =
      {
        expenses: "Expenses",
        income: "Income",
        debts: "Debts",
        shopping: "Shopping & Groceries",
        goals: "Goals",
        transactions: "Transactions",
        tax: "Tax Calculator",
        mindmap: "Mind Map",
        mortgage: "Mortgage Calculator",
        wife: "Wife Value Calculator",
      }[pageType] || pageType;
    return `Share ${label}`;
  })();

  return (
    <>
      <ShareLinkDialog
        open={open}
        onClose={onClose}
        title={shareDialogTitle}
        shareType={shareType}
        onShareTypeChange={(value) => setShareType(value as ShareType)}
        shareOptions={shareOptions}
        nameLabel="Name"
        nameValue={shareName}
        nameRequired={true}
        namePlaceholder={`e.g., ${
          {
            expenses: "Client expenses",
            income: "Income review",
            debts: "Debt snapshot",
            shopping: "Shopping list",
            forecasting: "Forecast profile",
            housing: "Housing scenario",
            mortgage: "Mortgage scenario",
            goals: "Goals overview",
            transactions: "Transaction list",
            tax: "Tax snapshot",
            mindmap: "Mind map",
            wife: "Wife value snapshot",
          }[pageType] || "Share link"
        }`}
        onNameChange={(value) => {
          setShareName(value);
          console.debug("[PageShareModal] Share name updated:", value);
        }}
        password={password}
        onPasswordChange={setPassword}
        showPasswordFor={["password"]}
        generatedLink={generatedLink}
        onCopy={() => handleCopy(generatedLink)}
        onGenerate={handleCreate}
        error={error}
        creating={creating}
        actionLabel="Create Link"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle2">Expiration</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Expires</InputLabel>
            <Select
              value={expirationOption}
              label="Expires"
              onChange={(e) =>
                setExpirationOption(e.target.value as ExpirationOption)
              }
            >
              <MenuItem value="1day">In 1 day</MenuItem>
              <MenuItem value="7days">In 7 days</MenuItem>
              <MenuItem value="30days">In 30 days</MenuItem>
              <MenuItem value="custom">Custom date/time</MenuItem>
              <MenuItem value="never">Never</MenuItem>
            </Select>
          </FormControl>
          {expirationOption === "custom" && (
            <TextField
              label="Custom Expiration"
              type="datetime-local"
              size="small"
              value={customExpiration}
              onChange={(e) => setCustomExpiration(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        {pageType === "debts" &&
          renderDebtSettings(debtSettings, setDebtSettings)}
        {pageType === "expenses" &&
          renderExpenseSettings(expenseSettings, setExpenseSettings)}
        {pageType === "income" &&
          renderIncomeSettings(incomeSettings, setIncomeSettings)}
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Existing Links
        </Typography>
        {shares.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No share links created.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {shares.map((share) => {
              const link = buildShareUrl(share.id);
              return (
                <Box
                  key={share.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {share.label || "Untitled share"}
                      {share.createdAt && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            color: "rgba(255,255,255,0.65)",
                            backgroundColor: "rgba(255,255,255,0.12)",
                          }}
                        >
                          {formatShareDateTime(share.createdAt)}
                        </Box>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describeShare(share)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {link}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => handleCopy(link)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {["debts", "expenses", "income"].includes(pageType) && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(share)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(share.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </ShareLinkDialog>
      {editingShare && (
        <Dialog
          open={Boolean(editingShare)}
          onClose={() => setEditingShare(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Edit Share</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                label="Name"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                required
                size="small"
              />
              {pageType === "debts" &&
                renderDebtSettings(editingSettings, setEditingSettings)}
              {pageType === "expenses" &&
                renderExpenseSettings(
                  editingExpenseSettings,
                  setEditingExpenseSettings
                )}
              {pageType === "income" &&
                renderIncomeSettings(
                  editingIncomeSettings,
                  setEditingIncomeSettings
                )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingShare(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default PageShareModal;
